import React, { useState } from "react";
import { Badge, Card, IconGlyph, SectionLabel } from "./ProfileDashboardUI";

const ACTIVITY_COLORS = {
  zero: "#EEEDE9",
  one: "#FFCBA0",
  medium: "#FD6600",
  high: "#C44F00",
};

const parseDate = (dateValue = "") => {
  const [year, month, day] = String(dateValue).split("-").map(Number);
  return Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
    ? new Date(year, month - 1, day)
    : null;
};

const formatDayMonth = (dateValue) => {
  const parsed = parseDate(dateValue);
  if (!parsed) return "-";

  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const formatStreakRange = (streak = {}) => {
  if (!streak.count) return "-";
  if (streak.start === streak.end) return formatDayMonth(streak.start);

  return `${formatDayMonth(streak.start)} - ${formatDayMonth(streak.end)}`;
};

const getZeroActivityColor = (tokens) => tokens.cardSoft || ACTIVITY_COLORS.zero;

const isDarkTheme = (tokens = {}) => tokens.page === "#111315" || tokens.card === "#1B1E22";

const getBarColor = (count = 0, tokens) => {
  if (count <= 0) return getZeroActivityColor(tokens);
  if (count === 1) return ACTIVITY_COLORS.one;
  if (count <= 3) return ACTIVITY_COLORS.medium;
  return ACTIVITY_COLORS.high;
};

const getHeatmapColor = (count = 0, tokens) => {
  if (count <= 0) return getZeroActivityColor(tokens);

  if (isDarkTheme(tokens)) {
    if (count === 1) return "rgba(253, 102, 0, 0.28)";
    if (count === 2) return "rgba(253, 102, 0, 0.46)";
    if (count <= 4) return "rgba(253, 102, 0, 0.7)";
    return ACTIVITY_COLORS.medium;
  }

  if (count === 1) return ACTIVITY_COLORS.one;
  if (count === 2) return "rgba(253, 102, 0, 0.4)";
  if (count <= 4) return "rgba(253, 102, 0, 0.7)";
  return ACTIVITY_COLORS.medium;
};

const Tooltip = ({ tooltip }) => {
  if (!tooltip) return null;

  return (
    <div
      className="pointer-events-none fixed z-50 max-w-[260px] rounded-lg px-3 py-2 text-xs font-medium text-white shadow-xl"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        backgroundColor: "#111111",
        transform: "translate(-50%, -108%)",
      }}
    >
      <div className="mb-1 font-semibold">{tooltip.title}</div>
      <div>{tooltip.count} RFQ{tooltip.count === 1 ? "" : "s"}</div>
      {tooltip.rfqs?.length > 0 && (
        <div className="mt-1 space-y-0.5 font-normal">
          {tooltip.rfqs.slice(0, 5).map((rfq) => (
            <div key={`${rfq.rfqId}-${rfq.name}`} className="truncate">
              - {rfq.name} #{rfq.rfqId}
            </div>
          ))}
          {tooltip.rfqs.length > 5 && <div>+{tooltip.rfqs.length - 5} more</div>}
        </div>
      )}
    </div>
  );
};

const WeeklyBar = ({ day, maxCount, onHover, onLeave, tokens }) => {
  const count = Number(day.count || 0);
  const barWidth = count > 0 ? Math.max(28, (count / maxCount) * 64) : 56;
  const color = getBarColor(count, tokens);

  return (
    <button
      type="button"
      className="group flex min-w-[72px] flex-1 flex-col items-center justify-end rounded-lg px-2 py-1 transition-colors"
      style={{ color: tokens.ink }}
      onMouseEnter={(event) => onHover(event, {
        title: day.label,
        count,
        rfqs: day.rfqs || [],
      })}
      onMouseMove={(event) => onHover(event, {
        title: day.label,
        count,
        rfqs: day.rfqs || [],
      })}
      onMouseLeave={onLeave}
    >
      <div className="flex h-[34px] items-end">
        <span
          className="block h-[4px] rounded-full"
          style={{ width: barWidth, backgroundColor: color }}
        />
      </div>
      <span className="mt-1 text-sm font-bold" style={{ color: count ? ACTIVITY_COLORS.medium : tokens.muted }}>
        {count || "-"}
      </span>
      <span className="text-[11px] font-semibold" style={{ color: count ? tokens.ink : tokens.muted }}>
        {day.dayName}
      </span>
      <span className="text-[10px] leading-tight" style={{ color: tokens.muted }}>{day.label}</span>
    </button>
  );
};

const HeatmapCell = ({ day, weekStart, onHover, onLeave, tokens }) => (
  <button
    type="button"
    className="h-6 rounded-md transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-orange-300"
    style={{ backgroundColor: getHeatmapColor(day.count, tokens) }}
    onMouseEnter={(event) => onHover(event, {
      title: `${day.dayName}, week of ${formatDayMonth(weekStart)}`,
      count: day.count || 0,
    })}
    onMouseMove={(event) => onHover(event, {
      title: `${day.dayName}, week of ${formatDayMonth(weekStart)}`,
      count: day.count || 0,
    })}
    onMouseLeave={onLeave}
    aria-label={`${day.dayName} ${day.label}: ${day.count || 0} RFQs`}
  />
);

const SummaryTile = ({ value, label, subLabel, tokens }) => (
  <div className="flex-1 border-r px-4 py-3 text-center last:border-r-0" style={{ borderColor: tokens.line }}>
    <div className="text-2xl font-bold" style={{ color: tokens.brand }}>{value}</div>
    <div className="mt-1 text-xs font-medium" style={{ color: tokens.ink }}>{label}</div>
    {subLabel && <div className="text-[11px]" style={{ color: tokens.muted }}>{subLabel}</div>}
  </div>
);

const WeeklyActivityStreak = ({ stats, tokens }) => {
  const activity = stats.rfqCreationActivity || stats.streak || {};
  const currentWeek = activity.currentWeek || { days: [], total: 0, label: "" };
  const heatmap = activity.heatmap || { weeks: [], dayLabels: [] };
  const summary = activity.summary || {};
  const [tooltip, setTooltip] = useState(null);

  const maxWeekCount = Math.max(1, ...currentWeek.days.map((day) => Number(day.count || 0)));
  const longestStreak = summary.longestStreak || {};
  const mostActiveDay = summary.mostActiveDay || {};

  const showTooltip = (event, details) => {
    setTooltip({
      ...details,
      x: event.clientX,
      y: event.clientY,
    });
  };

  return (
    <>
    <SectionLabel tokens={tokens}>My activity</SectionLabel>
    <Card tokens={tokens} className="relative overflow-visible">
      <Tooltip tooltip={tooltip} />

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: tokens.ink }}>My activity - RFQ creation log</h2>
          <p className="text-xs" style={{ color: tokens.muted }}>Day-wise count of RFQs created on Neevay</p>
        </div>
        <Badge variant="orange" tokens={tokens}>
          <IconGlyph name="flame" className="h-3.5 w-3.5" color={tokens.brand} />
          {longestStreak.count || 0} day streak
        </Badge>
      </div>

      <section className="rounded-xl border px-4 py-4" style={{ borderColor: tokens.line, backgroundColor: tokens.card }}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: tokens.muted }}>
            This week - {currentWeek.label}
          </span>
          <span className="text-xs font-bold" style={{ color: tokens.brand }}>
            {currentWeek.total || 0} RFQs created
          </span>
        </div>
        <div className="dashboard-print-expand-scroll mt-1 flex gap-3 overflow-x-auto pb-1">
          {currentWeek.days.map((day) => (
            <WeeklyBar
              key={day.date || day.key}
              day={day}
              maxCount={maxWeekCount}
              onHover={showTooltip}
              onLeave={() => setTooltip(null)}
              tokens={tokens}
            />
          ))}
        </div>
      </section>

      <section className="mt-3 rounded-xl border p-4" style={{ borderColor: tokens.line, backgroundColor: tokens.card }}>
        <div className="mb-4 flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: tokens.muted }}>
            Last 8 weeks - day x week heatmap
          </span>
          <span className="text-xs font-bold" style={{ color: tokens.brand }}>darker = more RFQs</span>
        </div>

        <div className="grid grid-cols-[40px_1fr] gap-x-2">
          <div />
          <div className="grid grid-cols-8 gap-1.5 pb-1">
            {heatmap.weeks.map((week) => (
              <span key={week.weekStart} className="text-[10px]" style={{ color: tokens.muted }}>
                {week.monthLabel}
              </span>
            ))}
          </div>

          {(heatmap.dayLabels || []).map((label, dayIndex) => (
            <React.Fragment key={label}>
              <div className="flex h-6 items-center text-[11px]" style={{ color: tokens.muted }}>{label}</div>
              <div className="grid grid-cols-8 gap-1.5">
                {(heatmap.weeks || []).map((week) => (
                  <HeatmapCell
                    key={`${week.weekStart}-${label}`}
                    day={week.days?.[dayIndex] || { dayName: label, count: 0 }}
                    weekStart={week.weekStart}
                    onHover={showTooltip}
                    onLeave={() => setTooltip(null)}
                    tokens={tokens}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-1.5 text-[11px]" style={{ color: tokens.muted }}>
          <span>Less</span>
          {[0, 1, 2, 4, 5].map((count) => (
            <span
              key={count}
              className="h-3.5 w-3.5 rounded"
              style={{ backgroundColor: getHeatmapColor(count, tokens) }}
            />
          ))}
          <span>More RFQs</span>
        </div>
      </section>

      <section className="mt-3 flex flex-col rounded-xl border sm:flex-row" style={{ borderColor: tokens.line, backgroundColor: tokens.card }}>
        <SummaryTile
          value={mostActiveDay.dayName || "-"}
          label="Most active day"
          subLabel={`avg ${mostActiveDay.average || 0} RFQs`}
          tokens={tokens}
        />
        <SummaryTile
          value={longestStreak.count || 0}
          label="Day longest streak"
          subLabel={formatStreakRange(longestStreak)}
          tokens={tokens}
        />
        <SummaryTile
          value={summary.thisWeekCount || 0}
          label="RFQs this week"
          subLabel="Mon to today"
          tokens={tokens}
        />
        <SummaryTile
          value={summary.totalLastEightWeeks || 0}
          label="Total RFQs"
          subLabel="last 8 weeks"
          tokens={tokens}
        />
      </section>
    </Card>
    </>
  );
};

export default WeeklyActivityStreak;
